import { useState, useRef, type ReactNode, type MouseEvent, type TouchEvent } from 'react';
import { ChevronsLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageComparisonProps {
  itemLeft: ReactNode;
  itemRight: ReactNode;
  labelLeft?: string;
  labelRight?: string;
  className?: string;
}

/**
 * Usage:
 * <ImageComparison
 *   itemLeft={<img src="new.jpg" />}
 *   itemRight={<img src="old.jpg" />}
 *   labelLeft="Después"
 *   labelRight="Antes"
 * />
 */
export function ImageComparison({
  itemLeft,
  itemRight,
  labelLeft,
  labelRight,
  className,
}: ImageComparisonProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePosition = (clientX: number) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    
    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    setSliderPosition(clampedPercentage);
  };

  // Mouse event handlers
  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    updatePosition(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = () => {
    setIsDragging(true);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !e.touches[0]) return;
    updatePosition(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full h-full overflow-hidden select-none cursor-ew-resize', className)}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'none' }}
      role="slider"
      aria-label={`Comparación: ${labelLeft || 'Izquierda'} vs ${labelRight || 'Derecha'}`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={sliderPosition}
      tabIndex={0}
    >
      {/* Item Left - Clipped from 0% to slider position */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
        }}
      >
        {itemLeft}
      </div>

      {/* Item Right - Clipped from slider position to 100% */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          clipPath: `inset(0 0 0 ${sliderPosition}%)`,
        }}
      >
        {itemRight}
      </div>

      {/* Divider Line & Handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-2xl cursor-ew-resize"
        style={{ left: `${sliderPosition}%` }}
      >
        {/* Handle Circle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-gray-200 hover:border-inboca-teal transition-colors">
          <ChevronsLeftRight className="h-6 w-6 text-gray-700" />
        </div>
      </div>

      {/* Optional Labels */}
      {labelLeft && (
        <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 rounded-md text-sm font-semibold backdrop-blur-sm">
          {labelLeft}
        </div>
      )}
      {labelRight && (
        <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-md text-sm font-semibold backdrop-blur-sm">
          {labelRight}
        </div>
      )}
    </div>
  );
}
